/* eslint-disable max-lines-per-function */
import './CoralPdpHowItWorks.scss'
import '../../../coral-library/css/_layout.scss'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

import { Modal, useIsWindowBiggerThan } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import { CTA } from '@websrc/App/Components/CTA'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapCoralWebPdpHowItWorks } from '@websrc/DataMappers/mapSpot'
import React, { FC, useContext, useState } from 'react'
import { Keyboard, Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

import { CheckIcon } from '../../../coral-library/icons/Check.icon'
import { CoralPdp5eModel } from './CoralPdp5eModel'
type CoralWebPdpHowItWorksProps = ReturnType<typeof mapCoralWebPdpHowItWorks>

export const CoralPdpHowItWorks: FC<CoralWebPdpHowItWorksProps> = ({
  asset,
  model_5e,
  cards,
  header,
  description,
  cta,
  split_contents,
}) => {
  const { matomoPageTemplate } = useContext(AppProviderContext)
  const card_variant =
    cards.length > 0 && cards.length < 5 ? (`v${cards.length}` as 'v1' | 'v2' | 'v3' | 'v4') : 'v4'
  const isMobile = !useIsWindowBiggerThan(719)
  const [slideHeight, setSlideHeight] = useState(0)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const handleCTAClick = () => {
    setIsModalVisible(true)
    matomo.trackEvent(
      'engagement',
      matomoPageTemplate,
      ['coral PDP HowItWorks'],
      ['coral 5E button'],
      'button'
    )
  }

  return (
    <div className={'container-coral-spot m--top-spacing-6xl'}>
      <div className={'how-it-works'}>
        {isModalVisible && (
          <Modal
            closeCallback={() => setIsModalVisible(false)}
            overlayBackgroundColor={'dark'}
            position={'center'}
          >
            {model_5e && <CoralPdp5eModel model_5e={model_5e} />}
          </Modal>
        )}
        <div className={'how-it-works__top-section-wrapper'}>
          <Asset
            className={'how-it-works__main-asset'}
            lazyLoading={true}
            objectFit={'cover'}
            srcSetWidths={[400, 600, 800, 1200, 1600]}
            thumbnail={asset?.thumbnail}
            video={asset?.video}
          />

          {isMobile ? (
            <div className={'how-it-works__swiper-cards'}>
              <Swiper
                className={'how-it-works__swiper'}
                keyboard={{ enabled: true }}
                modules={[Navigation, Keyboard]}
                navigation={true}
                onResize={(swiper) => {
                  const maxHeight = Math.max(...swiper.slides.map((slide) => slide.clientHeight))
                  setSlideHeight(maxHeight - 1)
                }}
                onSwiper={(swiper) => {
                  const maxHeight = Math.max(...swiper.slides.map((slide) => slide.clientHeight))
                  setSlideHeight(maxHeight - 1)
                }}
                pagination={{ clickable: true }}
                slidesPerView={'auto'}
                spaceBetween={16}
              >
                {cards.map((card, index) => {
                  return (
                    <SwiperSlide key={index}>
                      <div
                        className={`how-it-works__card m--${card_variant}`}
                        style={{ minHeight: slideHeight + 'px' }}
                      >
                        <div className={`how-it-works__card-top-wrapper`}>
                          <div className={`how-it-works__card-label`}>{card.label}</div>
                          <div>
                            {card.asset?.thumbnail && (
                              <Asset
                                className={'how-it-works__card-asset'}
                                lazyLoading={true}
                                objectFit={'cover'}
                                srcSetWidths={[400, 600, 800, 1200, 1600]}
                                thumbnail={card.asset.thumbnail}
                                video={card.asset?.video}
                              />
                            )}
                          </div>
                        </div>
                        <div className={`how-it-works__card-bottom-wrapper`}>
                          <div className={'how-it-works__card-header'}>{card.header}</div>
                          <p className={'how-it-works__card-content'}>{card.content}</p>
                          <div className={'how-it-works__card-cta-button-wrapper'}>
                            {card.button?.text && (
                              <button className={'how-it-works__card-cta'} onClick={handleCTAClick}>
                                {card.button.text}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  )
                })}
              </Swiper>
            </div>
          ) : (
            <div className={'how-it-works__cards'}>
              {cards.map((card, index) => {
                return (
                  <div className={`how-it-works__card m--${card_variant}`} key={index}>
                    <div className={`how-it-works__card-label`}>{card.label}</div>
                    <div>
                      {card.asset?.thumbnail && (
                        <Asset
                          className={'how-it-works__card-asset'}
                          lazyLoading={true}
                          objectFit={'cover'}
                          srcSetWidths={[400, 600, 800, 1200, 1600]}
                          thumbnail={card.asset.thumbnail}
                          video={card.asset?.video}
                        />
                      )}
                    </div>
                    <div className={`how-it-works__card-bottom-wrapper`}>
                      <div className={'how-it-works__card-header'}>{card.header}</div>
                      <p className={'how-it-works__card-content'}>{card.content}</p>
                      <div className={'how-it-works__card-cta-button-wrapper'}>
                        {card.button?.text && (
                          <button className={'how-it-works__card-cta'} onClick={handleCTAClick}>
                            {card.button.text}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
        <div className={'how-it-works__bottom-section-wrapper'}>
          <div className={'how-it-works__header-section-wrapper'}>
            <div className={'how-it-works__header'}>{header}</div>
            <div className={'how-it-works__content-wrapper'}>
              <div className={'how-it-works__description'}>{description}</div>
              <div className={'how-it-works__cta-button-wrapper'}>
                {cta?.text && (
                  <CTA
                    className={'cta-override-button how-it-works'}
                    cta={cta}
                    onClick={() => {
                      matomo.trackEvent(
                        'engagement',
                        matomoPageTemplate,
                        ['coral PDP HowItWorks'],
                        ['coral download PDF button'],
                        'button'
                      )
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          <div className={'how-it-works__split-contents'}>
            {split_contents.map((split_content, index) => (
              <div
                className={`how-it-works__split-content m--${split_content.asset_placement}`}
                key={index}
              >
                <Asset
                  className={'how-it-works__split-asset'}
                  lazyLoading={true}
                  objectFit={'contain'}
                  srcSetWidths={[400, 600, 800, 1200, 1600]}
                  thumbnail={split_content.asset?.thumbnail}
                  video={split_content.asset?.video}
                />
                <div className={'how-it-works__split-content-wrapper'}>
                  <div
                    className={`how-it-works__split-content-innter-wrapper m--${split_content.asset_placement}`}
                  >
                    <div className={'how-it-works__split-header'}>{split_content.header}</div>
                    <div className={'how-it-works__split-list-items-wrapper'}>
                      {split_content.list?.map((list_item, index) => {
                        return (
                          <div className={'how-it-works__split-list-item-wrapper'} key={index}>
                            <CheckIcon className={'how-it-works__list-item-icon'} />
                            <p className={'how-it-works__split-list-item-text'}>
                              {list_item.label}
                            </p>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
